import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { DialogClose, colorPalette } from '@qualifio/ui-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoyaltyProgramEmbed, ManagerApiClient } from '@loyalty/shared-common/manager-api-client';
import { ProgramAttributes } from '@loyalty/shared-common/gamification-api-client';
import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { faHtml5 } from '@fortawesome/free-brands-svg-icons/faHtml5';
import { faLink } from '@fortawesome/pro-solid-svg-icons/faLink';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';

interface Props extends DialogProps {
  accountId: string;
  api: ManagerApiClient;
  program?: ProgramAttributes;
  onClose: () => void;
}

const useStyles = makeStyles<Theme>(({ spacing, palette }) => {
  return {
    description: {
      textAlign: 'center',
    },
    cardContent: {
      alignItems: 'center',
      backgroundColor: palette.marine.main,
      color: palette.secondary.contrastText,
      display: 'flex',
      padding: `${spacing(2)} !important`,
      '&:last-child': {
        paddingBottom: `${spacing(2)} !important`,
      },
    },
    cardField: {
      backgroundColor: 'transparent',
      border: 'none',
      color: 'inherit',
      height: 40,
      outline: 'none',
      width: '100%',
    },
    cardFieldWrapper: {
      flex: 1,
      position: 'relative',
      '&:after': {
        background: `linear-gradient(270.28deg, #12384A 38.94%, rgba(18, 56, 74, 0) 92.71%);`,
        content: '""',
        display: 'block',
        height: '100%',
        position: 'absolute',
        right: 0,
        top: 0,
        width: '25%',
      },
    },
    cardCopyButton: {
      marginLeft: 'auto',
    },
    tooltip: {
      backgroundColor: colorPalette.orange.main,
    },
    tooltipArrow: {
      color: colorPalette.orange.main,
    },
    card: {
      marginTop: spacing(2),
      marginBottom: spacing(2),
      border: `1px solid ${palette.grey.main}`,
      boxShadow: 'none !important',
    },
  };
});

export function LoyaltyProgramEmbedDialog({
  onClose,
  accountId,
  api,
  program,
  open,
  ...props
}: Props): React.ReactElement {
  const { t } = useTranslation();
  const classes = useStyles();

  const inputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [selectedTab, selectTab] = useState('iframe');
  const [copied, setCopied] = useState(false);
  const [data, setData] = useState<LoyaltyProgramEmbed | null>(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleFocus = ({ target }: React.FocusEvent<HTMLInputElement>) => {
    target.select();
  };

  const handleCopy = () => {
    const doc = document as any;
    const input = inputRef.current;

    if (input) {
      input.focus();

      if (input.setSelectionRange) {
        input.setSelectionRange(0, input.value.length);
      } else {
        input.select();
      }

      doc.execCommand('copy');

      if (doc.selection) {
        doc.selection.empty();
      } else if (window.getSelection) {
        window.getSelection()?.removeAllRanges();
      }

      input.blur();
      setCopied(true);
    }
  };

  const handleTabChange = (event: React.ChangeEvent<any>, value: any) => {
    selectTab(value);
  };

  const getValue = () => {
    switch (selectedTab) {
      case 'script':
        return data?.script;
      case 'iframe':
        return data?.iframe;
      default:
        return data?.webview;
    }
  };

  useEffect(() => {
    if (program && program.id) {
      setLoading(true);
      api
        .getEmbed(program.id)
        .then(setData)
        .catch(err => setErrorMessage(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [accountId, api, program]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth {...props}>
      <DialogTitle>
        {t('loyalty.embed.title')}
        <DialogClose close={onClose} />
      </DialogTitle>
      <DialogContent>
        <p className={classes.description}>{t('loyalty.embed.description')}</p>

        <Card className={classes.card}>
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab value="iframe" label="HTML" icon={<FontAwesomeIcon icon={faHtml5} />} />
            <Tab value="webview" label="Webview" icon={<FontAwesomeIcon icon={faLink} />} />
          </Tabs>
          <CardContent className={classes.cardContent}>
            {loading && <div style={{ padding: '13px 0', lineHeight: 1 }}>Loading...</div>}
            {errorMessage && <Typography color="error">{errorMessage}</Typography>}
            {!loading && !errorMessage && (
              <>
                <div className={classes.cardFieldWrapper}>
                  <input
                    value={getValue()}
                    readOnly
                    ref={inputRef}
                    onFocus={handleFocus}
                    className={classes.cardField}
                  />
                </div>
                <div className={classes.cardCopyButton}>
                  <Tooltip
                    open={copied}
                    title={t('generic.copied')}
                    placement="top"
                    leaveDelay={1000}
                    onClose={() => setCopied(false)}
                    classes={{
                      tooltip: classes.tooltip,
                      arrow: classes.tooltipArrow,
                    }}
                  >
                    <Button color="secondary" onClick={handleCopy}>
                      <FontAwesomeIcon icon={faCopy} />
                    </Button>
                  </Tooltip>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
